/**
 * Характеристики навыка героя: сила (`skillPower`), перезарядка (`skillCooldown`), число снарядов
 * (`projectileCount`). Источники — звёзды (`SkillDef.starUpgrades`, звезда k → элемент k−1) и модификаторы боя
 * (Tech, усиления «Мощь навыков», «Быстрые навыки», «Мультивыстрел»). Порядок применения — как у `applyStat`.
 */
import type { BalanceData, HeroId, SkillDef, StatModifier } from "@newfungame/contracts";
import { PERMILLE } from "@newfungame/contracts";
import { mulPermille } from "../sim/math";
import { applyStat } from "../sim/stats";

/** Модификаторы от звёзд навыка: первые `stars` наборов `starUpgrades`. */
export function starModifiers(skill: SkillDef, stars: number): StatModifier[] {
  return skill.starUpgrades.slice(0, Math.max(0, stars)).flat();
}

export interface SkillStats {
  /** Сила навыка, промилле (1000 — как в данных). */
  power: number;
  /** Перезарядка, тиков. */
  cooldownTicks: number;
  /** Рост урона навыка с уровнем героя, промилле (`damagePerLevelPermille`, как у обычной атаки). */
  levelGrowth: number;
}

export function skillStats(
  balance: BalanceData,
  skill: SkillDef,
  heroId: HeroId,
  level: number,
  stars: number,
  mods: readonly StatModifier[],
): SkillStats {
  const all = [...mods, ...starModifiers(skill, stars)];
  const def = balance.heroes.find((h) => h.id === heroId);
  return {
    power: Math.max(0, applyStat(PERMILLE, "skillPower", heroId, all)),
    cooldownTicks: Math.max(1, applyStat(skill.cooldownTicks, "skillCooldown", heroId, all)),
    levelGrowth: PERMILLE + (def?.damagePerLevelPermille ?? 0) * Math.max(0, level - 1),
  };
}

/** Урон навыка: базовый × рост с уровнем героя × сила навыка (не меньше 1). */
export function skillDamage(base: number, st: SkillStats): number {
  return Math.max(1, mulPermille(mulPermille(base, st.levelGrowth), st.power));
}

/** Снарядов за выстрел обычной атаки героя (`projectileCount`, не меньше 1). */
export function projectileCount(heroId: HeroId, mods: readonly StatModifier[]): number {
  return Math.max(1, applyStat(1, "projectileCount", heroId, mods));
}
